import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { ArrowLeft, CheckCircle, Link2, Download, RefreshCw, Trash2, Edit2, X, FlaskConical, FileSpreadsheet } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { Button, ThemedText, ConfirmDialog } from '../components';
import labReportService, { getLabReportStatusLabel, getLabSampleTypeLabel } from '../api/services/labReportService';
import { LabReportStatusColors, withOpacity } from '../constants/statusColors';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel, StyledSelect as Select } from '../components/FormStyles';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const HeaderTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};
  &:hover { opacity: 1; background-color: ${props => props.theme.colors.primary}10; }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const MetaRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const StatusBadge = styled.span`
  padding: 6px ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  display: inline-block;
  ${props => {
    const c = LabReportStatusColors[props.$status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const ConfidenceBadge = styled.span`
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  ${props => {
    const val = props.$value;
    if (val >= 0.8) return 'background-color: #dcfce7; color: #166534;';
    if (val >= 0.5) return 'background-color: #fef3c7; color: #92400e;';
    return 'background-color: #fee2e2; color: #991b1b;';
  }}
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Tab = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border: none;
  background: transparent;
  color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.muted};
  font-weight: ${props => props.$active ? '600' : '400'};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? props.theme.colors.tint : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.15s;
  &:hover { color: ${props => props.theme.colors.text}; }
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  @media (max-width: 1024px) { grid-template-columns: 1fr; }
`;

const PdfViewer = styled.iframe`
  width: 100%;
  height: 700px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.theme.colors.background};
`;

const DataPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  max-height: 700px;
  overflow-y: auto;
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
`;

// Results grid
const GridContainer = styled.div`
  overflow-x: auto;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
`;

const GridTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const GridTh = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const GridTd = styled.td`
  padding: 6px 12px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
`;

const CellValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s;
  ${props => {
    if (props.$edited) return `background-color: #dbeafe; color: #1e40af;`;
    if (props.$confidence != null && props.$confidence < 0.5) return `background-color: #fee2e2; color: #991b1b;`;
    if (props.$confidence != null && props.$confidence < 0.8) return `background-color: #fef3c7; color: #92400e;`;
    return '';
  }}
  &:hover { background-color: ${props => props.theme.colors.hover}; }
`;

const SampleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const SectionCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const RawJsonPre = styled.pre`
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
  font-size: 12px;
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
  word-break: break-all;
`;

const MatchCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  gap: ${props => props.theme.spacing.md};
`;

const StyledInput = styled.input`
  padding: 6px 10px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 13px;
  width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.muted};
  gap: ${props => props.theme.spacing.md};
`;

const ErrorBanner = styled.div`
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: #991b1b;
  font-size: ${props => props.theme.typography.sizes.small};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const MatchReasonPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => props.$type === 'sampleId' ? '#dcfce7' : '#dbeafe'};
  color: ${props => props.$type === 'sampleId' ? '#166534' : '#1e40af'};
  white-space: nowrap;
`;

const ScoreBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ScoreDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => {
    if (props.$score >= 0.7) return '#16a34a';
    if (props.$score >= 0.4) return '#d97706';
    return '#6b7280';
  }};
`;

const MatchReasonBox = styled.div`
  padding: 10px 14px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  margin-top: 10px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  width: 100%;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

export const LabReportDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isSuperAdmin } = useRole();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [activeTab, setActiveTab] = useState('results');
  const [approving, setApproving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Linked submission state
  const [linkedSubmission, setLinkedSubmission] = useState(null);
  const [linkedSubmissionLoading, setLinkedSubmissionLoading] = useState(false);
  const [linkingSubmission, setLinkingSubmission] = useState(false);
  const [rematching, setRematching] = useState(false);

  // Candidates state (for Suggested tab and ManualLinkModal)
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [showManualLinkModal, setShowManualLinkModal] = useState(false);
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const manualSearchTimer = React.useRef(null);

  // Sample-level linking state
  const [linkingSampleId, setLinkingSampleId] = useState(null);
  const [sampleCandidates, setSampleCandidates] = useState([]);
  const [sampleCandidatesLoading, setSampleCandidatesLoading] = useState(false);
  const [sampleSearchQuery, setSampleSearchQuery] = useState('');
  const sampleSearchTimer = React.useRef(null);

  // Edit result modal
  const [editResult, setEditResult] = useState(null);
  const [editForm, setEditForm] = useState({ rawValue: '', qualifier: '', reviewNote: '' });
  const [saving, setSaving] = useState(false);

  // Raw data (lazy-loaded)
  const [rawData, setRawData] = useState(null);
  const [loadingRawData, setLoadingRawData] = useState(false);

  // Edit sample modal
  const [editSample, setEditSample] = useState(null);
  const [sampleForm, setSampleForm] = useState({ sampleInfo: '', sampleMatrix: '', sampleType: '' });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await labReportService.getLabReportById(id);
      setReport(response?.data || response);
    } catch {
      toast.error('Failed to load lab report');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchPdfUrl = useCallback(async () => {
    try {
      const response = await labReportService.getPdfUrl(id);
      const data = response?.data || response;
      setPdfUrl(data.url || data);
    } catch { /* PDF may not be available */ }
  }, [id]);

  useEffect(() => {
    fetchReport();
    fetchPdfUrl();
  }, [fetchReport, fetchPdfUrl]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await labReportService.approveLabReport(id);
      toast.success('Lab report approved');
      fetchReport();
    } catch {
      toast.error('Failed to approve lab report');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await labReportService.deleteLabReport(id);
      toast.success('Lab report deleted');
      navigate('/lab-reports');
    } catch {
      toast.error('Failed to delete lab report');
    }
  };

  const openEditResult = (result) => {
    setEditResult(result);
    setEditForm({
      rawValue: result.rawValue || '',
      qualifier: result.qualifier || '',
      reviewNote: result.reviewNote || '',
    });
  };

  const saveEditResult = async () => {
    if (!editResult) return;
    setSaving(true);
    try {
      await labReportService.updateResult(id, editResult.id, editForm);
      toast.success('Result updated');
      setEditResult(null);
      fetchReport();
    } catch {
      toast.error('Failed to update result');
    } finally {
      setSaving(false);
    }
  };

  const openEditSample = (sample) => {
    setEditSample(sample);
    setSampleForm({
      sampleInfo: sample.sampleInfo || '',
      sampleMatrix: sample.sampleMatrix || '',
      sampleType: sample.sampleType || '',
    });
  };

  const saveEditSample = async () => {
    if (!editSample) return;
    setSaving(true);
    try {
      await labReportService.updateSample(id, editSample.id, sampleForm);
      toast.success('Sample updated');
      setEditSample(null);
      fetchReport();
    } catch {
      toast.error('Failed to update sample');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteResult = async (resultId) => {
    try {
      await labReportService.deleteResult(id, resultId);
      toast.success('Result deleted');
      fetchReport();
    } catch {
      toast.error('Failed to delete result');
    }
  };

  const loadLinkedSubmission = useCallback(async () => {
    setLinkedSubmissionLoading(true);
    try {
      const data = await labReportService.getLinkedSubmission(id);
      // 204 No Content → data is "" or null; check for valid object with submissionId
      setLinkedSubmission(data && data.submissionId ? data : null);
    } catch {
      setLinkedSubmission(null);
    } finally {
      setLinkedSubmissionLoading(false);
    }
  }, [id]);

  const handleLinkSubmission = async (submissionId, reason) => {
    setLinkingSubmission(true);
    try {
      await labReportService.linkSubmission(id, submissionId, reason);
      toast.success('Submission linked successfully');
      handleCloseManualModal();
      fetchReport();
      loadLinkedSubmission();
    } catch {
      toast.error('Failed to link submission');
    } finally {
      setLinkingSubmission(false);
    }
  };

  const handleUnlinkSubmission = async () => {
    try {
      await labReportService.unlinkSubmission(id);
      toast.success('Submission unlinked');
      setLinkedSubmission(null);
      fetchReport();
    } catch {
      toast.error('Failed to unlink submission');
    }
  };

  const handleAttemptRematch = async () => {
    setRematching(true);
    try {
      await labReportService.attemptMatch(id);
      toast.success('Re-match attempt complete');
      fetchReport();
      loadLinkedSubmission();
    } catch {
      toast.error('Re-match failed');
    } finally {
      setRematching(false);
    }
  };

  const loadCandidates = useCallback(async (query) => {
    setCandidatesLoading(true);
    try {
      const result = await labReportService.getSubmissionCandidates(id, query || undefined);
      setCandidates(result?.data || result || []);
    } catch {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, [id]);

  const handleManualSearchChange = (e) => {
    const q = e.target.value;
    setManualSearchQuery(q);
    clearTimeout(manualSearchTimer.current);
    manualSearchTimer.current = setTimeout(() => loadCandidates(q), 400);
  };

  const handleOpenManualModal = () => {
    setManualSearchQuery('');
    setShowManualLinkModal(true);
    loadCandidates('');
  };

  const handleCloseManualModal = () => {
    setShowManualLinkModal(false);
    setCandidates([]);
    setManualSearchQuery('');
  };

  // Sample-level linking handlers
  const handleOpenSampleLinkModal = (sampleId) => {
    setLinkingSampleId(sampleId);
    setSampleSearchQuery('');
    setSampleCandidates([]);
    loadSampleCandidates(sampleId, '');
  };

  const handleCloseSampleLinkModal = () => {
    setLinkingSampleId(null);
    setSampleCandidates([]);
    setSampleSearchQuery('');
  };

  const loadSampleCandidates = async (sampleId, query) => {
    setSampleCandidatesLoading(true);
    try {
      const result = await labReportService.getSampleCandidates(id, sampleId, query || undefined);
      setSampleCandidates(result?.data || result || []);
    } catch {
      setSampleCandidates([]);
    } finally {
      setSampleCandidatesLoading(false);
    }
  };

  const handleSampleSearchChange = (e) => {
    const q = e.target.value;
    setSampleSearchQuery(q);
    clearTimeout(sampleSearchTimer.current);
    sampleSearchTimer.current = setTimeout(() => loadSampleCandidates(linkingSampleId, q), 400);
  };

  const handleLinkSample = async (sampleId, submissionId, reason) => {
    setLinkingSubmission(true);
    try {
      await labReportService.linkSampleSubmission(id, sampleId, submissionId, reason);
      toast.success('Sample linked');
      handleCloseSampleLinkModal();
      fetchReport();
    } catch {
      toast.error('Failed to link sample');
    } finally {
      setLinkingSubmission(false);
    }
  };

  const handleUnlinkSample = async (sampleId) => {
    try {
      await labReportService.unlinkSampleSubmission(id, sampleId);
      toast.success('Sample unlinked');
      fetchReport();
    } catch {
      toast.error('Failed to unlink sample');
    }
  };

  const handleUnlinkAll = async () => {
    try {
      await labReportService.unlinkSubmission(id);
      toast.success('All links cleared');
      setLinkedSubmission(null);
      fetchReport();
    } catch {
      toast.error('Failed to unlink');
    }
  };

  // Build pivot data: rows=samples, cols=analytes
  const buildPivotData = () => {
    if (!report) return { analytes: [], samples: [], resultMap: {} };
    const analytes = [];
    const analyteMap = {};
    (report.sections || []).forEach(s => {
      (s.analytes || []).forEach(a => {
        if (!analyteMap[a.id]) {
          analyteMap[a.id] = { ...a, sectionName: s.name };
          analytes.push(analyteMap[a.id]);
        }
      });
    });

    const samples = [...(report.samples || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const resultMap = {};
    samples.forEach(s => {
      (s.testResults || []).forEach(r => {
        resultMap[`${s.id}-${r.labAnalyteId}`] = r;
      });
    });

    return { analytes, samples, resultMap };
  };

  if (loading) {
    return (
      <Layout>
        <PageContainer theme={theme}>
          <ThemedText $color="muted">Loading...</ThemedText>
        </PageContainer>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <PageContainer theme={theme}>
          <EmptyState theme={theme}>
            <FlaskConical size={48} />
            <ThemedText $size="lg">Lab report not found</ThemedText>
            <Button onClick={() => navigate('/lab-reports')}>Back to Lab Reports</Button>
          </EmptyState>
        </PageContainer>
      </Layout>
    );
  }

  const { analytes, samples, resultMap } = buildPivotData();
  const isReviewable = report.status === 'NeedsReview';

  return (
    <Layout>
      <PageContainer theme={theme}>
        {/* Header */}
        <HeaderSection theme={theme}>
          <HeaderTopRow>
            <BackButton theme={theme} onClick={() => navigate('/lab-reports')}>
              <ArrowLeft size={14} /> Lab Reports
            </BackButton>
          </HeaderTopRow>
          <HeaderTitleRow theme={theme}>
            <HeaderTitleContent theme={theme}>
              <ThemedText $size="h2" $weight="bold">
                {report.reportNumber || report.originalFileName}
              </ThemedText>
              <StatusBadge theme={theme} $status={report.status}>
                {getLabReportStatusLabel(report.status)}
              </StatusBadge>
              {report.overallConfidence != null && (
                <ConfidenceBadge $value={report.overallConfidence}>
                  {(report.overallConfidence * 100).toFixed(0)}% confidence
                </ConfidenceBadge>
              )}
            </HeaderTitleContent>
            <HeaderActions theme={theme}>
              {isReviewable && (
                <Button onClick={handleApprove} disabled={approving}>
                  <CheckCircle size={14} style={{ marginRight: 4 }} />
                  {approving ? 'Approving...' : 'Approve'}
                </Button>
              )}
              {pdfUrl && (
                <Button $variant="secondary" onClick={() => window.open(pdfUrl, '_blank')}>
                  <Download size={14} style={{ marginRight: 4 }} />
                  {report.sourceType === 'Excel' ? 'Download Excel' : 'View PDF'}
                </Button>
              )}
              <Button $variant="ghost" onClick={() => { fetchReport(); fetchPdfUrl(); }}>
                <RefreshCw size={14} />
              </Button>
              <Button $variant="ghost" onClick={() => setDeleteConfirm(true)}>
                <Trash2 size={14} />
              </Button>
            </HeaderActions>
          </HeaderTitleRow>
          <MetaRow theme={theme}>
            {report.labName && <MetaItem>Lab: <strong>{report.labName}</strong></MetaItem>}
            {report.lotId && <MetaItem>Lot: {report.lotId}</MetaItem>}
            {report.projectName && <MetaItem>Project: {report.projectName}</MetaItem>}
            {report.dateReported && <MetaItem>Reported: {new Date(report.dateReported).toLocaleDateString()}</MetaItem>}
            {report.dateReceived && <MetaItem>Received: {new Date(report.dateReceived).toLocaleDateString()}</MetaItem>}
            {report.customerName && <MetaItem>Customer: {report.customerName}</MetaItem>}
            {report.locationName && <MetaItem>Location: {report.locationName}</MetaItem>}
            {report.workOrderNumber && <MetaItem>WO: {report.workOrderNumber}</MetaItem>}
            <MetaItem>Samples: {report.sampleCount || samples.length}</MetaItem>
            {report.detectedFormatCode && report.detectedFormatCode !== 'unknown' && (
              <MetaItem>
                Format: <strong>{report.formatName || report.detectedFormatCode}</strong>
                {report.wasAutoDetected && <span style={{ fontSize: 11, opacity: 0.7 }}> (auto)</span>}
              </MetaItem>
            )}
            {report.detectionConfidence != null && report.detectedFormatCode && report.detectedFormatCode !== 'unknown' && (
              <MetaItem>Detection: {(report.detectionConfidence * 100).toFixed(0)}%</MetaItem>
            )}
          </MetaRow>
        </HeaderSection>

        {report.errorMessage && (
          <ErrorBanner theme={theme}>{report.errorMessage}</ErrorBanner>
        )}

        {/* Tabs */}
        <TabBar theme={theme}>
          <Tab theme={theme} $active={activeTab === 'results'} onClick={() => setActiveTab('results')}>Results</Tab>
          <Tab theme={theme} $active={activeTab === 'samples'} onClick={() => setActiveTab('samples')}>Samples ({samples.length})</Tab>
          <Tab theme={theme} $active={activeTab === 'sections'} onClick={() => setActiveTab('sections')}>Sections</Tab>
          <Tab theme={theme} $active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>Overview</Tab>
          <Tab theme={theme} $active={activeTab === 'linked'} onClick={() => { setActiveTab('linked'); loadLinkedSubmission(); if (report?.matchStatus === 'Suggested') loadCandidates(); }}>
            Sample Linking
            {report.matchStatus && report.matchStatus !== 'Unmatched' && (
              <span style={{
                marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                backgroundColor: report.matchStatus === 'AutoLinked' ? '#dcfce7' : report.matchStatus === 'Suggested' ? '#fef3c7' : '#dbeafe',
                color: report.matchStatus === 'AutoLinked' ? '#166534' : report.matchStatus === 'Suggested' ? '#92400e' : '#1e40af',
              }}>
                {(() => {
                  const linked = (report.samples || []).filter(s => s.formSubmissionId).length;
                  const total = (report.samples || []).length;
                  return `${linked}/${total}`;
                })()}
              </span>
            )}
          </Tab>
          {isSuperAdmin() && (
            <Tab theme={theme} $active={activeTab === 'raw'} onClick={() => {
              setActiveTab('raw');
              if (!rawData && !loadingRawData) {
                setLoadingRawData(true);
                labReportService.getRawData(id).then(res => {
                  setRawData(res?.data || res);
                }).catch(() => {}).finally(() => setLoadingRawData(false));
              }
            }}>Raw Data</Tab>
          )}
        </TabBar>

        {/* Results Tab - Split layout with PDF + pivot grid */}
        {activeTab === 'results' && (
          <SplitLayout theme={theme}>
            <div>
              {report.sourceType === 'Excel' ? (
                <Card theme={theme}>
                  <EmptyState theme={theme}>
                    <FileSpreadsheet size={36} color={theme.colors.muted} />
                    <ThemedText $color="muted">Preview not available for Excel files</ThemedText>
                    {pdfUrl && (
                      <Button $variant="secondary" $size="small" onClick={() => window.open(pdfUrl, '_blank')}>
                        <Download size={14} style={{ marginRight: 4 }} /> Download Excel
                      </Button>
                    )}
                  </EmptyState>
                </Card>
              ) : pdfUrl ? (
                <PdfViewer theme={theme} src={pdfUrl} title="Lab Report PDF" />
              ) : (
                <Card theme={theme}>
                  <EmptyState theme={theme}>
                    <ThemedText $color="muted">PDF not available</ThemedText>
                  </EmptyState>
                </Card>
              )}
            </div>
            <DataPanel theme={theme}>
              {analytes.length === 0 ? (
                <Card theme={theme}>
                  <EmptyState theme={theme}>
                    <ThemedText $color="muted">No results extracted yet</ThemedText>
                  </EmptyState>
                </Card>
              ) : (
                <GridContainer theme={theme}>
                  <GridTable>
                    <thead>
                      <tr>
                        <GridTh theme={theme} style={{ position: 'sticky', left: 0, zIndex: 2, backgroundColor: theme.colors.backgroundSecondary }}>
                          Sample
                        </GridTh>
                        {analytes.map(a => (
                          <GridTh key={a.id} theme={theme} title={`${a.sectionName} | ${a.name} (${a.units || ''})`}>
                            {a.name}
                            {a.units && <span style={{ fontWeight: 400, opacity: 0.7 }}> ({a.units})</span>}
                          </GridTh>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {samples.map(s => (
                        <tr key={s.id}>
                          <GridTd theme={theme} style={{ position: 'sticky', left: 0, backgroundColor: theme.colors.backgroundSecondary, fontWeight: 500, zIndex: 1 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              {s.formSubmissionId ? (
                                <CheckCircle size={12} style={{ color: '#16a34a', flexShrink: 0 }} title={`Linked to ${s.linkedFormName || 'submission'}`} />
                              ) : (
                                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', backgroundColor: theme.colors.border, flexShrink: 0 }} title="Not linked" />
                              )}
                              <span>
                                {s.referenceNumber}
                                {s.sampleInfo && (
                                  <span style={{ display: 'block', fontWeight: 400, fontSize: 11, opacity: 0.7 }}>
                                    {s.sampleInfo}
                                  </span>
                                )}
                              </span>
                            </span>
                          </GridTd>
                          {analytes.map(a => {
                            const result = resultMap[`${s.id}-${a.id}`];
                            if (!result) return <GridTd key={a.id} theme={theme}>-</GridTd>;
                            return (
                              <GridTd key={a.id} theme={theme}>
                                <CellValue
                                  theme={theme}
                                  $confidence={result.confidence}
                                  $edited={result.isManuallyEdited}
                                  onClick={() => openEditResult(result)}
                                  title={`Click to edit${result.confidence != null ? ` | Confidence: ${(result.confidence * 100).toFixed(0)}%` : ''}${result.isManuallyEdited ? ' | Manually edited' : ''}`}
                                >
                                  {result.qualifier}{result.rawValue || '-'}
                                  {result.isManuallyEdited && <Edit2 size={10} />}
                                </CellValue>
                              </GridTd>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </GridTable>
                </GridContainer>
              )}

            </DataPanel>
          </SplitLayout>
        )}

        {/* Samples Tab */}
        {activeTab === 'samples' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {samples.length === 0 ? (
              <EmptyState theme={theme}>
                <ThemedText $color="muted">No samples extracted</ThemedText>
              </EmptyState>
            ) : (
              samples.map(s => (
                <SampleRow key={s.id} theme={theme}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <ThemedText $weight="bold">{s.referenceNumber}</ThemedText>
                    <ThemedText $size="small" $color="muted">
                      {s.sampleInfo || '-'} | Matrix: {s.sampleMatrix || '-'} | Type: {getLabSampleTypeLabel(s.sampleType) || '-'}
                    </ThemedText>
                    {s.sampledAt && (
                      <ThemedText $size="small" $color="muted">
                        Sampled: {new Date(s.sampledAt).toLocaleDateString()}
                      </ThemedText>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <ThemedText $size="small" $color="muted">
                      {(s.testResults || []).length} results
                    </ThemedText>
                    <Button $variant="ghost" $size="small" onClick={() => openEditSample(s)}>
                      <Edit2 size={14} />
                    </Button>
                  </div>
                </SampleRow>
              ))
            )}
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(report.sections || []).length === 0 ? (
              <EmptyState theme={theme}>
                <ThemedText $color="muted">No sections extracted</ThemedText>
              </EmptyState>
            ) : (
              (report.sections || []).map(s => (
                <SectionCard key={s.id} theme={theme}>
                  <ThemedText $weight="bold" style={{ marginBottom: 8 }}>{s.name}</ThemedText>
                  {s.description && <ThemedText $size="small" $color="muted" style={{ marginBottom: 8 }}>{s.description}</ThemedText>}
                  {(s.analytes || []).length > 0 && (
                    <GridContainer theme={theme}>
                      <GridTable>
                        <thead>
                          <tr>
                            <GridTh theme={theme}>Analyte</GridTh>
                            <GridTh theme={theme}>Method</GridTh>
                            <GridTh theme={theme}>Units</GridTh>
                            <GridTh theme={theme}>Detection Limit</GridTh>
                          </tr>
                        </thead>
                        <tbody>
                          {s.analytes.map(a => (
                            <tr key={a.id}>
                              <GridTd theme={theme}><strong>{a.name}</strong>{a.description && <span style={{ color: theme.colors.muted, marginLeft: 6, fontSize: 12 }}>{a.description}</span>}</GridTd>
                              <GridTd theme={theme}>{a.methodReference || '-'}</GridTd>
                              <GridTd theme={theme}>{a.units || '-'}</GridTd>
                              <GridTd theme={theme}>{a.detectionLimit || '-'}</GridTd>
                            </tr>
                          ))}
                        </tbody>
                      </GridTable>
                    </GridContainer>
                  )}
                </SectionCard>
              ))
            )}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Card theme={theme}>
              <CardTitle theme={theme}>Report Details</CardTitle>
              <InfoGrid theme={theme}>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Report Number</InfoLabel><InfoValue theme={theme}>{report.reportNumber || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Lot ID</InfoLabel><InfoValue theme={theme}>{report.lotId || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Lab</InfoLabel><InfoValue theme={theme}>{report.labName || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Project</InfoLabel><InfoValue theme={theme}>{report.projectName || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Date Reported</InfoLabel><InfoValue theme={theme}>{report.dateReported ? new Date(report.dateReported).toLocaleDateString() : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Date Received</InfoLabel><InfoValue theme={theme}>{report.dateReceived ? new Date(report.dateReceived).toLocaleDateString() : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Date Sampled</InfoLabel><InfoValue theme={theme}>{report.dateSampled ? new Date(report.dateSampled).toLocaleDateString() : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Original File</InfoLabel><InfoValue theme={theme}>{report.originalFileName}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>File Size</InfoLabel><InfoValue theme={theme}>{report.fileSizeBytes ? `${(report.fileSizeBytes / 1024 / 1024).toFixed(1)} MB` : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Template</InfoLabel><InfoValue theme={theme}>{report.templateName || 'Auto-detect'}</InfoValue></InfoItem>
              </InfoGrid>
            </Card>
            <Card theme={theme}>
              <CardTitle theme={theme}>Processing & Links</CardTitle>
              <InfoGrid theme={theme}>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Status</InfoLabel><InfoValue theme={theme}><StatusBadge theme={theme} $status={report.status}>{getLabReportStatusLabel(report.status)}</StatusBadge></InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Confidence</InfoLabel><InfoValue theme={theme}>{report.overallConfidence != null ? <ConfidenceBadge $value={report.overallConfidence}>{(report.overallConfidence * 100).toFixed(0)}%</ConfidenceBadge> : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Customer</InfoLabel><InfoValue theme={theme}>{report.customerName || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Location</InfoLabel><InfoValue theme={theme}>{report.locationName || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Work Order</InfoLabel><InfoValue theme={theme}>{report.workOrderNumber || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Reviewed By</InfoLabel><InfoValue theme={theme}>{report.reviewedBy || '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Reviewed At</InfoLabel><InfoValue theme={theme}>{report.reviewedAt ? new Date(report.reviewedAt).toLocaleString() : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Created</InfoLabel><InfoValue theme={theme}>{new Date(report.createdAt).toLocaleString()}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Processing Started</InfoLabel><InfoValue theme={theme}>{report.processingStartedAt ? new Date(report.processingStartedAt).toLocaleString() : '-'}</InfoValue></InfoItem>
                <InfoItem theme={theme}><InfoLabel theme={theme}>Processing Completed</InfoLabel><InfoValue theme={theme}>{report.processingCompletedAt ? new Date(report.processingCompletedAt).toLocaleString() : '-'}</InfoValue></InfoItem>
              </InfoGrid>
              {report.reviewComments && (
                <div style={{ marginTop: 12 }}>
                  <InfoLabel theme={theme}>Review Comments</InfoLabel>
                  <ThemedText style={{ marginTop: 4 }}>{report.reviewComments}</ThemedText>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Sample Linking Tab */}
        {activeTab === 'linked' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Summary Card */}
            <Card theme={theme}>
              <CardTitle theme={theme}>
                <span>
                  <Link2 size={14} style={{ marginRight: 6 }} />
                  Sample Linking Summary
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button $size="small" onClick={handleAttemptRematch} disabled={rematching}>
                    <RefreshCw size={14} style={{ marginRight: 4 }} />
                    {rematching ? 'Matching...' : 'Auto-Match All'}
                  </Button>
                  {(report.samples || []).some(s => s.formSubmissionId) && (
                    <Button $variant="ghost" $size="small" onClick={handleUnlinkAll}>
                      Unlink All
                    </Button>
                  )}
                </div>
              </CardTitle>
              {(() => {
                const linked = (report.samples || []).filter(s => s.formSubmissionId).length;
                const total = (report.samples || []).length;
                const pct = total > 0 ? Math.round((linked / total) * 100) : 0;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: theme.colors.border, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, backgroundColor: linked === total && total > 0 ? '#16a34a' : '#d97706', transition: 'width 0.3s' }} />
                    </div>
                    <ThemedText $weight="bold" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      {linked} / {total} samples linked
                    </ThemedText>
                  </div>
                );
              })()}
              {report.formSubmissionId && (() => {
                const linkedSample = (report.samples || []).find(s => s.linkedFormSchemaId);
                const schemaId = linkedSample?.linkedFormSchemaId;
                return (
                  <div style={{ fontSize: 13, color: theme.colors.muted }}>
                    Report-level submission:{' '}
                    {schemaId ? (
                      <strong
                        onClick={() => navigate(`/forms/${schemaId}/submissions/${report.formSubmissionId}`)}
                        style={{ color: theme.colors.primary, cursor: 'pointer' }}
                      >
                        #{report.formSubmissionId}
                      </strong>
                    ) : (
                      <strong>#{report.formSubmissionId}</strong>
                    )}
                    {report.matchReason && <span> — {report.matchReason}</span>}
                  </div>
                );
              })()}
            </Card>

            {/* Sample Link Table */}
            <GridContainer theme={theme}>
              <GridTable>
                <thead>
                  <tr>
                    <GridTh theme={theme}>Parent Ref</GridTh>
                    <GridTh theme={theme}>Full Reference</GridTh>
                    <GridTh theme={theme}>Description</GridTh>
                    <GridTh theme={theme}>Linked To</GridTh>
                    <GridTh theme={theme}>Match Reason</GridTh>
                    <GridTh theme={theme} style={{ textAlign: 'right' }}>Actions</GridTh>
                  </tr>
                </thead>
                <tbody>
                  {(report.samples || []).map(s => (
                    <tr key={s.id}>
                      <GridTd theme={theme} style={{ fontWeight: 500, color: s.parentReferenceNumber ? theme.colors.text : theme.colors.muted }}>
                        {s.parentReferenceNumber || '-'}
                      </GridTd>
                      <GridTd theme={theme}>
                        <code style={{ background: theme.colors.background, padding: '1px 6px', borderRadius: 4 }}>
                          {s.referenceNumber}
                        </code>
                      </GridTd>
                      <GridTd theme={theme} style={{ color: theme.colors.muted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.sampleInfo || '-'}
                      </GridTd>
                      <GridTd theme={theme}>
                        {s.formSubmissionId ? (
                          <span
                            onClick={() => {
                              const subId = s.linkedParentSubmissionId || s.formSubmissionId;
                              const schemaId = s.linkedFormSchemaId;
                              if (schemaId) navigate(`/forms/${schemaId}/submissions/${subId}`);
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, backgroundColor: '#dcfce7', color: '#166534', cursor: s.linkedFormSchemaId ? 'pointer' : 'default' }}
                          >
                            <CheckCircle size={12} />
                            {s.linkedFormName || 'Submission'} ({s.linkedSampleIdValue || `#${s.formSubmissionId}`})
                          </span>
                        ) : (
                          <span style={{ color: theme.colors.muted, fontSize: 12 }}>—</span>
                        )}
                      </GridTd>
                      <GridTd theme={theme} style={{ fontSize: 12, color: theme.colors.muted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.matchReason || '-'}
                      </GridTd>
                      <GridTd theme={theme} style={{ textAlign: 'right' }}>
                        {s.formSubmissionId ? (
                          <Button $variant="ghost" $size="small" onClick={() => handleUnlinkSample(s.id)} title="Unlink">
                            <X size={14} />
                          </Button>
                        ) : (
                          <Button $size="small" onClick={() => handleOpenSampleLinkModal(s.id)}>
                            <Link2 size={12} style={{ marginRight: 4 }} /> Link
                          </Button>
                        )}
                      </GridTd>
                    </tr>
                  ))}
                </tbody>
              </GridTable>
            </GridContainer>
          </div>
        )}

        {/* Raw Tab (SuperAdmin only) */}
        {activeTab === 'raw' && isSuperAdmin() && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {loadingRawData ? (
              <Card theme={theme}><CardTitle theme={theme}>Loading raw data...</CardTitle></Card>
            ) : (
              <>
                <Card theme={theme}>
                  <CardTitle theme={theme}>Textract Raw JSON</CardTitle>
                  <RawJsonPre theme={theme}>
                    {rawData?.rawTextractJson ? JSON.stringify(JSON.parse(rawData.rawTextractJson), null, 2) : 'No Textract data available'}
                  </RawJsonPre>
                </Card>
                <Card theme={theme}>
                  <CardTitle theme={theme}>AI Extraction JSON</CardTitle>
                  <RawJsonPre theme={theme}>
                    {rawData?.aiExtractionJson ? JSON.stringify(JSON.parse(rawData.aiExtractionJson), null, 2) : 'No AI extraction data available'}
                  </RawJsonPre>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Edit Result Modal */}
        <ModalOverlay $isOpen={!!editResult} onClick={() => setEditResult(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <ModalHeader theme={theme}>Edit Test Result</ModalHeader>
            <ModalBody theme={theme}>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Raw Value</FormLabel>
                <StyledInput theme={theme} value={editForm.rawValue}
                  onChange={e => setEditForm(f => ({ ...f, rawValue: e.target.value }))} />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Qualifier</FormLabel>
                <Select theme={theme} value={editForm.qualifier}
                  onChange={e => setEditForm(f => ({ ...f, qualifier: e.target.value }))}>
                  <option value="">None</option>
                  <option value="<">&lt; (Less than)</option>
                  <option value=">">&gt; (Greater than)</option>
                  <option value="~">~ (Approximate)</option>
                </Select>
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Review Note</FormLabel>
                <StyledInput theme={theme} value={editForm.reviewNote}
                  onChange={e => setEditForm(f => ({ ...f, reviewNote: e.target.value }))}
                  placeholder="Optional note about this correction" />
              </FormGroup>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="ghost" $size="small" onClick={() => handleDeleteResult(editResult?.id)}
                style={{ marginRight: 'auto' }}>
                <Trash2 size={14} style={{ marginRight: 4 }} /> Delete
              </Button>
              <Button $variant="secondary" onClick={() => setEditResult(null)}>Cancel</Button>
              <Button onClick={saveEditResult} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Edit Sample Modal */}
        <ModalOverlay $isOpen={!!editSample} onClick={() => setEditSample(null)}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <ModalHeader theme={theme}>Edit Sample</ModalHeader>
            <ModalBody theme={theme}>
              {editSample && (
                <ThemedText $size="small" $color="muted" style={{ marginBottom: 8 }}>
                  Reference: {editSample.referenceNumber}
                </ThemedText>
              )}
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Sample Info</FormLabel>
                <StyledInput theme={theme} value={sampleForm.sampleInfo}
                  onChange={e => setSampleForm(f => ({ ...f, sampleInfo: e.target.value }))} />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Matrix</FormLabel>
                <StyledInput theme={theme} value={sampleForm.sampleMatrix}
                  onChange={e => setSampleForm(f => ({ ...f, sampleMatrix: e.target.value }))} />
              </FormGroup>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Sample Type</FormLabel>
                <Select theme={theme} value={sampleForm.sampleType}
                  onChange={e => setSampleForm(f => ({ ...f, sampleType: e.target.value }))}>
                  <option value="">Unknown</option>
                  <option value="Field">Field</option>
                  <option value="QC_Blank">QC Blank</option>
                  <option value="QC_Spike">QC Spike</option>
                  <option value="QC_Duplicate">QC Duplicate</option>
                  <option value="QC_StandardReference">QC Standard Reference</option>
                </Select>
              </FormGroup>
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="secondary" onClick={() => setEditSample(null)}>Cancel</Button>
              <Button onClick={saveEditSample} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        <ConfirmDialog
          isOpen={deleteConfirm}
          title="Delete Lab Report"
          message="Are you sure you want to delete this lab report? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteConfirm(false)}
        />

        {/* Manual Link Modal */}
        <ModalOverlay $isOpen={showManualLinkModal} onClick={handleCloseManualModal}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <ModalHeader theme={theme}>Link a Submission Manually</ModalHeader>
            <ModalBody theme={theme}>
              <div style={{ marginBottom: 12 }}>
                <SearchInput
                  theme={theme}
                  placeholder="Search by Sample ID, form name, or submitter…"
                  value={manualSearchQuery}
                  onChange={handleManualSearchChange}
                  autoFocus
                />
              </div>
              {candidatesLoading ? (
                <ThemedText $color="muted" style={{ textAlign: 'center', padding: '24px 0' }}>
                  Searching…
                </ThemedText>
              ) : candidates.length === 0 ? (
                <ThemedText $color="muted" style={{ textAlign: 'center', padding: '24px 0' }}>
                  No submissions found — try adjusting your search
                </ThemedText>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                  {candidates.map(c => (
                    <MatchCard key={c.submissionId} theme={theme}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          {c.matchScore > 0 && (
                            <ScoreBar>
                              <ScoreDot $score={c.matchScore} />
                              <span style={{ fontSize: 11, color: theme.colors.muted, fontWeight: 600 }}>
                                {Math.round(c.matchScore * 100)}%
                              </span>
                            </ScoreBar>
                          )}
                          <ThemedText $weight="bold" style={{ fontSize: 13 }}>{c.formName}</ThemedText>
                        </div>
                        <div style={{ fontSize: 12, color: theme.colors.muted, marginBottom: 4 }}>
                          {c.submittedBy} · {new Date(c.submittedAt).toLocaleDateString()}
                          {c.sampleIdValue && <> · <code style={{ background: theme.colors.background, padding: '0 4px', borderRadius: 3 }}>{c.sampleIdValue}</code></>}
                          {c.workOrderNumber && <> · {c.workOrderNumber}</>}
                        </div>
                        {c.matchReasons.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {c.matchReasons.map((r, i) => (
                              <MatchReasonPill key={i} $type={r.toLowerCase().includes('sampleid') ? 'sampleId' : 'date'}>
                                {r}
                              </MatchReasonPill>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        $size="small"
                        onClick={() => handleLinkSubmission(c.submissionId, c.matchReasons.length > 0 ? `Manually linked: ${c.matchReasons.join(', ')}` : 'Manually linked')}
                        disabled={linkingSubmission}
                      >
                        <Link2 size={12} style={{ marginRight: 4 }} /> Link
                      </Button>
                    </MatchCard>
                  ))}
                </div>
              )}
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="secondary" onClick={handleCloseManualModal}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>

        {/* Per-Sample Link Modal */}
        <ModalOverlay $isOpen={!!linkingSampleId} onClick={handleCloseSampleLinkModal}>
          <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <ModalHeader theme={theme}>
              Link Sample {(() => {
                const s = (report?.samples || []).find(s => s.id === linkingSampleId);
                return s ? `"${s.referenceNumber}"` : '';
              })()} to a Submission
            </ModalHeader>
            <ModalBody theme={theme}>
              <div style={{ marginBottom: 12 }}>
                <SearchInput
                  theme={theme}
                  placeholder="Search by Sample ID, form name, or submitter…"
                  value={sampleSearchQuery}
                  onChange={handleSampleSearchChange}
                  autoFocus
                />
              </div>
              {sampleCandidatesLoading ? (
                <ThemedText $color="muted" style={{ textAlign: 'center', padding: '24px 0' }}>
                  Searching…
                </ThemedText>
              ) : sampleCandidates.length === 0 ? (
                <ThemedText $color="muted" style={{ textAlign: 'center', padding: '24px 0' }}>
                  No submissions found — try adjusting your search
                </ThemedText>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                  {sampleCandidates.map(c => (
                    <MatchCard key={c.submissionId} theme={theme}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                          {c.matchScore > 0 && (
                            <ScoreBar>
                              <ScoreDot $score={c.matchScore} />
                              <span style={{ fontSize: 11, color: theme.colors.muted, fontWeight: 600 }}>
                                {Math.round(c.matchScore * 100)}%
                              </span>
                            </ScoreBar>
                          )}
                          <ThemedText $weight="bold" style={{ fontSize: 13 }}>{c.formName}</ThemedText>
                          {c.parentFormSubmissionId && (
                            <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, backgroundColor: '#e0e7ff', color: '#3730a3', fontWeight: 600 }}>
                              Instance #{c.instanceIndex || 1}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: theme.colors.muted, marginBottom: 4 }}>
                          {c.submittedBy} · {new Date(c.submittedAt).toLocaleDateString()}
                          {c.sampleIdValue && <> · <code style={{ background: theme.colors.background, padding: '0 4px', borderRadius: 3 }}>{c.sampleIdValue}</code></>}
                          {c.workOrderNumber && <> · {c.workOrderNumber}</>}
                        </div>
                        {c.matchReasons.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {c.matchReasons.map((r, i) => (
                              <MatchReasonPill key={i} $type={r.toLowerCase().includes('match') ? 'sampleId' : 'date'}>
                                {r}
                              </MatchReasonPill>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        $size="small"
                        onClick={() => handleLinkSample(linkingSampleId, c.submissionId, c.matchReasons.length > 0 ? c.matchReasons.join(', ') : 'Manually linked')}
                        disabled={linkingSubmission}
                      >
                        <Link2 size={12} style={{ marginRight: 4 }} /> Link
                      </Button>
                    </MatchCard>
                  ))}
                </div>
              )}
            </ModalBody>
            <ModalFooter theme={theme}>
              <Button $variant="secondary" onClick={handleCloseSampleLinkModal}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </PageContainer>
    </Layout>
  );
};
